// SPDX-License-Identifier: MIT
// Client-side SAS input validation used by AzureProviderInterface to gate authentication UI.

import { isEncryptedSasToken } from './crypto';

const HOST_ALLOW_LIST = /^[a-z0-9-]{3,63}\.(blob|dfs)\.core\.windows\.net$/i;
const REQUIRED_QUERY_KEYS = ['sp', 'se', 'sig'] as const;
const SAS_PARAM_PATTERN = /([?&])([a-z]+)=/i;

type RequiredQueryKey = (typeof REQUIRED_QUERY_KEYS)[number];

export type SasValidationReason =
  | 'EMPTY'
  | 'INVALID_SCHEME'
  | 'INVALID_HOST'
  | 'MISSING_CONTAINER'
  | 'MISSING_PARAMS'
  | 'MISSING_CONTEXT'
  | 'PARSE_ERROR';

export interface SasValidationOptions {
  accountName?: string;
  containerName?: string;
}

export interface SasValidationResult {
  isValid: boolean;
  reason?: SasValidationReason;
}

const hasRequiredParams = (params: URLSearchParams): boolean =>
  REQUIRED_QUERY_KEYS.every(key => {
    const value = params.get(key as RequiredQueryKey);
    return typeof value === 'string' && value.trim().length > 0;
  });

const extractSearchParams = (token: string): URLSearchParams => {
  const normalized = token.startsWith('?') ? token.slice(1) : token;
  return new URLSearchParams(normalized);
};

export function validateSasInput(
  rawInput: string,
  { accountName, containerName }: SasValidationOptions = {}
): SasValidationResult {
  const input = rawInput.trim();
  if (!input) {
    return { isValid: false, reason: 'EMPTY' };
  }

  if (isEncryptedSasToken(input)) {
    return { isValid: true };
  }

  const schemeMatch = input.match(/^[A-Za-z][A-Za-z0-9+.-]*:/);
  if (schemeMatch && !input.toLowerCase().startsWith('https://')) {
    return { isValid: false, reason: 'INVALID_SCHEME' };
  }

  if (/^https?:\/\//i.test(input)) {
    try {
      const url = new URL(input);
      if (!HOST_ALLOW_LIST.test(url.hostname)) {
        return { isValid: false, reason: 'INVALID_HOST' };
      }
      const containerSegment = url.pathname.split('/').filter(Boolean)[0];
      if (!containerSegment) {
        return { isValid: false, reason: 'MISSING_CONTAINER' };
      }
      if (!hasRequiredParams(url.searchParams)) {
        return { isValid: false, reason: 'MISSING_PARAMS' };
      }
      return { isValid: true };
    } catch {
      return { isValid: false, reason: 'PARSE_ERROR' };
    }
  }

  if (!SAS_PARAM_PATTERN.test(input)) {
    return { isValid: false, reason: 'MISSING_PARAMS' };
  }

  const params = extractSearchParams(input);
  if (!hasRequiredParams(params)) {
    return { isValid: false, reason: 'MISSING_PARAMS' };
  }

  if (!accountName || !containerName) {
    return { isValid: false, reason: 'MISSING_CONTEXT' };
  }

  return { isValid: true };
}

const SAS_VALIDATION_MESSAGES: Record<SasValidationReason, string> = {
  EMPTY: 'Paste a SAS URL or token to continue.',
  INVALID_SCHEME: 'Only https:// SAS URLs are allowed.',
  INVALID_HOST: 'Use an Azure Blob or DFS endpoint (blob.core.windows.net / dfs.core.windows.net).',
  MISSING_CONTAINER: 'Container segment missing in SAS URL.',
  MISSING_PARAMS: 'SAS must include sp, se, and sig parameters.',
  MISSING_CONTEXT: 'Provide account + container or paste the full SAS URL.',
  PARSE_ERROR: 'Could not parse SAS token. Please verify and try again.',
};

export function getSasValidationMessage(reason?: SasValidationReason): string {
  if (!reason) return '';
  return SAS_VALIDATION_MESSAGES[reason] || 'Invalid SAS token';
}
